import * as THREE from 'three';
import type { FeatureResultMesh } from '../../mesh/feature';
import {
    collectImportedModelTopologyRepositories,
} from '../../imported-model/topology/imported-model-topology-runtime';
import type { Viewer } from '../Viewer';
import { ModelDisplayMode } from './ModelDisplayMode';


/**
 * 导入模型拓扑显示边控制器。
 *
 * 设计说明：
 * 1. 导入模型显示边必须来自 BRep/topologySnapshot，不允许从 GLB 三角网格反推；
 * 2. 控制器只负责找到 imported topology pick host，并打开或关闭其合并显示边；
 * 3. 点击选择仍由射线选取链路动态创建 FeatureLine / FeatureCurve 代理。
 */
export class ImportedModelTopologyDisplayEdgeController {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 当前由显示模式管理过的 pick host 集合。 */
    private readonly managedPickHostSet: Set<FeatureResultMesh> = new Set();

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 同步导入模型拓扑显示边。
     *
     * @param displayMode 当前模型显示模式。
     * @param edgeColor 显示边颜色。
     */
    public syncDisplayMode(
        displayMode: ModelDisplayMode,
        edgeColor: THREE.ColorRepresentation,
    ): void {
        const shouldCreateDisplayEdges = displayMode === ModelDisplayMode.ShadedWithEdges
            || displayMode === ModelDisplayMode.Unshaded;
        const nextManagedPickHostSet = new Set<FeatureResultMesh>();

        // 从场景根收集 imported topology 仓库，覆盖 imported-assembly 与组合装配的多实例场景。
        collectImportedModelTopologyRepositories(this.viewer.scene).forEach((repository) => {
            repository.listBodyBindingIds().forEach((bodyBindingId) => {
                const bodyRecord = repository.resolveBodyRecordByBindingId(bodyBindingId);
                if (!bodyRecord) {
                    return;
                }

                /**
                 * 实例 source Mesh 的默认边线由表面 Unit 使用共享 instanceMatrix 绘制。
                 * FeatureResultMesh 只保留点线面语义宿主职责，禁止继续创建 occurrence 级普通黑边。
                 */
                const instancedUnit = this.viewer.resolveImportedModelInstancedMeshUnit(
                    bodyRecord.visibleMesh,
                );
                if (instancedUnit?.setTopologyDisplayEdgesEnabled(
                    shouldCreateDisplayEdges,
                    edgeColor,
                )) {
                    bodyRecord.getPickHost()?.setTopologyDisplayEdgesEnabled(false, edgeColor);
                    return;
                }

                if (!shouldCreateDisplayEdges) {
                    /**
                     * Shaded 模式只需要隐藏默认边线，不应为了关闭显示边而创建新的 pick host。
                     * 已经存在的 pick host 会继续保留，避免 hover/selected 代理被释放。
                     */
                    const pickHost = bodyRecord.getPickHost();
                    if (pickHost) {
                        pickHost.setTopologyDisplayEdgesEnabled(false, edgeColor);
                        nextManagedPickHostSet.add(pickHost);
                    }
                    return;
                }

                // ensurePickHost 只创建隐藏拓扑宿主，显示边由宿主内部合并为单个 LineSegments。
                const pickHost = repository.ensurePickHost(bodyRecord);
                pickHost.setTopologyDisplayEdgesEnabled(true, edgeColor);
                nextManagedPickHostSet.add(pickHost);
            });
        });

        this.disableDetachedDisplayEdges(nextManagedPickHostSet);
        this.replaceManagedPickHosts(nextManagedPickHostSet);
    }

    /**
     * 释放控制器管理的显示边。
     */
    public dispose(): void {
        this.disableManagedDisplayEdges();
    }

    /**
     * 禁用当前管理过的全部显示边。
     */
    private disableManagedDisplayEdges(): void {
        this.managedPickHostSet.forEach((pickHost) => {
            pickHost.setTopologyDisplayEdgesEnabled(false);
        });
        this.managedPickHostSet.clear();
    }

    /**
     * 关闭已经不在当前 imported topology 仓库中的旧显示边。
     *
     * @param nextManagedPickHostSet 下一轮仍有效的 pick host 集合。
     */
    private disableDetachedDisplayEdges(nextManagedPickHostSet: ReadonlySet<FeatureResultMesh>): void {
        this.managedPickHostSet.forEach((pickHost) => {
            if (nextManagedPickHostSet.has(pickHost)) {
                return;
            }

            // 旧模型卸载或切换标签后，必须撤销旧 host 的显示边保留状态。
            pickHost.setTopologyDisplayEdgesEnabled(false);
        });
    }

    /**
     * 替换当前管理的 pick host 集合。
     *
     * @param nextManagedPickHostSet 下一轮管理集合。
     */
    private replaceManagedPickHosts(nextManagedPickHostSet: ReadonlySet<FeatureResultMesh>): void {
        this.managedPickHostSet.clear();
        nextManagedPickHostSet.forEach((pickHost) => {
            this.managedPickHostSet.add(pickHost);
        });
    }

}

